/**
 * Iter XX.89 — Paid Instagram Personalisation Gate.
 *
 * A tiny helper module that decides whether the 2-tap persona +
 * goal picker should render before the chat. Deliberately isolated
 * from Chat.jsx so the whole experiment can be dark-launched behind
 * `paid_instagram_personalisation_gate_enabled` and pulled entirely
 * by turning the flag off — no code path in Chat.jsx changes for
 * organic / direct / returning / signed-in visitors.
 *
 * Detection rule (founder brief, hard):
 *   · utm_source ∈ meta / instagram / facebook / ig / fb
 *   · AND utm_medium ∈ paid_social / cpc / paid / paid_meta /
 *                       paid_ig / ppc
 *   · document.referrer is NEVER consulted.
 *
 * Persistence:
 *   · Once completed OR skipped, `localStorage.g_persona_gate_v1_done`
 *     is set to "1" for that visitor forever. Zero re-prompts.
 *   · Signed-in visitors (localStorage.gully_token present) never
 *     see the gate at all — they're existing users.
 */
import { getStoredUtms } from "./claritySession";
import { hasToken } from "./nativeTokenStorage";

const DONE_KEY = "g_persona_gate_v1_done";
const CONTEXT_KEY = "g_persona_gate_v1_context";

const META_SOURCES = new Set([
  "instagram", "ig", "meta", "facebook", "fb",
]);
const PAID_MEDIUMS = new Set([
  "paid_social", "cpc", "paid", "paid_meta", "paid_ig", "ppc",
]);

/** True if the persisted UTMs describe a paid Meta ad click. */
export function isPaidMetaTraffic() {
  try {
    const utms = getStoredUtms() || {};
    const src = String(utms.utm_source || "").toLowerCase();
    const med = String(utms.utm_medium || "").toLowerCase();
    if (!src || !med) return false;
    return META_SOURCES.has(src) && PAID_MEDIUMS.has(med);
  } catch { return false; }
}

/** True if this visitor already saw the gate (completed OR skipped). */
export function isGateCompletedLocal() {
  try { return localStorage.getItem(DONE_KEY) === "1"; }
  catch { return false; }
}

/** True if this visitor has a persisted auth token (existing user). */
export function isSignedInLocal() {
  try { return hasToken(); }
  catch { return false; }
}

/**
 * Composite eligibility for showing the gate. Feature-flag check is
 * deliberately kept out of this helper — the caller reads the flag
 * via `useFlag("paid_instagram_personalisation_gate_enabled")` so the
 * gate hides instantly when the flag is toggled off.
 */
export function shouldShowPaidInstagramGate() {
  if (isSignedInLocal()) return false;
  if (isGateCompletedLocal()) return false;
  return isPaidMetaTraffic();
}

/**
 * Persist the persona + goal selections locally so the gate never
 * fires again for this visitor. Also stashes the raw context so
 * Chat.jsx can attach it to the FIRST /web/chat call.
 */
export function markGateCompleted({ persona, goal }) {
  try {
    localStorage.setItem(DONE_KEY, "1");
    if (persona) {
      localStorage.setItem(
        CONTEXT_KEY,
        JSON.stringify({ persona, goal: goal || null, ts: Date.now() }),
      );
    }
  } catch { /* noop */ }
}

/** Read the stashed persona context, or null if none. */
export function readStashedPersonaContext() {
  try {
    const raw = localStorage.getItem(CONTEXT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.persona) return null;
    return { persona: parsed.persona, goal: parsed.goal || null };
  } catch { return null; }
}

/**
 * Consume the stashed persona context — returns it and deletes it in
 * one call, so it's only ever attached to the FIRST /web/chat request.
 * Callers should treat a null return as "nothing to attach".
 */
export function consumeStashedPersonaContext() {
  const ctx = readStashedPersonaContext();
  if (!ctx) return null;
  try { localStorage.removeItem(CONTEXT_KEY); } catch { /* noop */ }
  return ctx;
}
